import React, { memo, useEffect, useState, useCallback, useMemo } from "react";
import "./WhatsappCampaignModal.scss";
import { Form, Modal, Switch, Skeleton } from "antd";
import CustomSelect from "../../../Reuseable/Select/Select";
import InputField from "../../../Reuseable/InputField/InputField";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchPortfolioMessageTemplates } from "../../../assets/api/campaignService";
import CustomNotification from "../../../Reuseable/CustomNotification/CustomNotification";
import { fetchUserFilters } from "../../../store/filters";

const WhatsappCampaignModal = ({ isOpen, onClose, onSave, load, isEdit, initialData }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { token } = useSelector((state) => state.auth);
  
  const [messageTemplateOptions, setMessageTemplateOptions] = useState([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const { filter_list } = useSelector((state) => state.filters);

  const portfolioOptions = useMemo(() => {
    const portfoliosData =
      filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return portfoliosData.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  useEffect(() => {
    if (isOpen) {
      if (isEdit && initialData) {
        form.setFieldsValue({
          name: initialData.name,
          portfolio_id: initialData.portfolio?.id,
          message_template_id: initialData.message_template?.id,
          all_cases: initialData.all_cases || false,
        });

        // Fetch templates for the pre-selected portfolio
        if (initialData.portfolio?.id && token) {
          setIsTemplatesLoading(true);
          fetchPortfolioMessageTemplates(token, initialData.portfolio.id, "whatsapp")
            .then((res) => {
              const fetchedResults = res?.data?.data || [];
              const formattedOptions = fetchedResults.map((t) => ({
                value: t.id,
                label: t.name,
              }));
              setMessageTemplateOptions(formattedOptions);
            })
            .catch((err) => {
              console.error("Error fetching templates by portfolio:", err);
              setMessageTemplateOptions([]);
            })
            .finally(() => {
              setIsTemplatesLoading(false);
            });
        }
      } else {
        dispatch(fetchUserFilters());
        form.resetFields();
        setMessageTemplateOptions([]);
      }
    } else {
      form.resetFields();
      setMessageTemplateOptions([]);
    }
  }, [isOpen, isEdit, initialData, form, token]);

  const handlePortfolioChange = useCallback((portfolioId) => {
    form.setFieldsValue({ message_template_id: undefined });
    if (portfolioId && token) {
      setIsTemplatesLoading(true);
      fetchPortfolioMessageTemplates(token, portfolioId, "whatsapp")
        .then((res) => {
          console.log(res?.data?.data, "====fetchPortfolioMessageTemplates====")
          const fetchedResults = res?.data?.data || [];
          const formattedOptions = fetchedResults.map((t) => ({
            value: t.id,
            label: t.name,
          }));
          setMessageTemplateOptions(formattedOptions);
        })
        .catch((err) => {
          console.error("Error fetching templates by portfolio:", err);
          CustomNotification.show("Failed to fetch message templates", "error");
          setMessageTemplateOptions([]);
        })
        .finally(() => {
          setIsTemplatesLoading(false);
        });
    } else {
      setMessageTemplateOptions([]);
    }
  }, [form, token]);

  const handleSave = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        let payload = {};

        if (isEdit && initialData) {
          // In edit mode, only send fields that have changed
          if (values.name !== initialData.name) {
            payload.name = values.name;
          }
          
          if (values.message_template_id !== initialData.message_template?.id) {
            payload.message_template_id = values.message_template_id;
          }
        } else {
          // In add mode, construct the full exact payload requested
          payload = {
            name: values.name,
            portfolio_id: values.portfolio_id,
            message_template_id: values.message_template_id,
            all_cases: values.all_cases !== undefined ? values.all_cases : true,
          };
        }

        onSave?.(payload);
      })
      .catch((info) => {
        console.error("Validation Failed:", info);
      });
  }, [form, isEdit, initialData, onSave]);

  return (
    <Modal
      className="whatsappCampaignModalMainContainer"
      title={isEdit ? "Edit Whatsapp Campaign" : "Add Whatsapp Campaign"}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
    >
      <div className="mainWrapperForModal">
        <Form
          form={form}
          layout="vertical"
          initialValues={{ all_cases: true }}
        >
          <div className="selectorWrapper">
            <div className="wrappedContainer">
              <Form.Item
                name="name"
                className="formItem"
                label="Name"
                rules={[{ required: true, message: "Please enter campaign name" }]}
              >
                <InputField placeholder="Enter campaign name" />
              </Form.Item>
            </div>
          </div>

          {/* The two spacer <div>s that used to hold this row at 50% are gone;
              the row is a grid, so one field simply fills it. */}
          <div className="selectorWrapper">
            {!isEdit && (
              <div className="wrappedContainer">
                <Form.Item
                  name="portfolio_id"
                  className="formItem"
                  label="Portfolio"
                  rules={[{ required: true, message: "Please select a portfolio" }]}
                >
                  <CustomSelect
                    options={portfolioOptions || []}
                    placeholder="Select portfolio"
                    onChange={handlePortfolioChange}
                  />
                </Form.Item>
              </div>
            )}
            <div className="wrappedContainer">
              <Form.Item
                name="message_template_id"
                className="formItem"
                label="Message Template"
                rules={[{ required: true, message: "Please select a message template" }]}
              >
                {isTemplatesLoading && isEdit ? (
                  <Skeleton.Input active block style={{ height: "38px", borderRadius: "4px" }} />
                ) : (
                  <CustomSelect 
                  options={isTemplatesLoading
                    ? [
                        {
                          label: <Skeleton.Input active size="small" block />,
                          value: "loading-1",
                          disabled: true,
                        },
                        {
                          label: <Skeleton.Input active size="small" block />,
                          value: "loading-2",
                          disabled: true,
                        },
                        {
                          label: <Skeleton.Input active size="small" block />,
                          value: "loading-3",
                          disabled: true,
                        },
                      ]
                    : messageTemplateOptions
                  } 
                    placeholder="Select message template"
                  loading={isTemplatesLoading}
                  />
                )}
              </Form.Item>
            </div>
          </div>

          {!isEdit && (
            /* The toggle used to carry its row layout in a style attribute on
               the Form.Item, and had no explanation of what it does. */
            <div className="waAllCases">
              <div className="waAllCases__copy">
                <span className="waAllCases__label">All Cases</span>
                <span className="waAllCases__hint">
                  Send to every case in the portfolio. Turn off to target a subset.
                </span>
              </div>
              <Form.Item name="all_cases" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </div>
          )}

          <div className="submitBtn">
            {/* NormalButton defaults to width="100%" as an inline style, so the
                size is set through its own prop rather than fought in CSS. */}
            <NormalButton
              type="default"
              className="waCancelBtn"
              width="auto"
              controlHeight={40}
              onClick={onClose}
            >
              Cancel
            </NormalButton>
            <NormalButton
              type="primary"
              className="waSubmitBtn"
              width="auto"
              controlHeight={40}
              onClick={handleSave}
              loading={load}
            >
              {isEdit ? "Save Changes" : "Create Campaign"}
            </NormalButton>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default memo(WhatsappCampaignModal);